/**
 * lot DD — règle UNIQUE de lecture du rôle (`type_contact`).
 * Le rôle existe en codes (`acquereur`, `proprietaire`…) et en libellés (« Acquéreur »,
 * « Acquéreur/Propriétaire »…). On compare toujours la forme normalisée.
 * Miroir de `backend/roles_contact.py`.
 */
export function normaliserRole(v) {
  return (v || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s/-]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

export const ROLES_ACQUEREUR = [
  'acquereur', 'acquereur_proprietaire', 'acquereur_vendeur',
  'prospect', 'prospect_acquereur', 'investisseur',
];

export const ROLES_MANDANT = [
  'proprietaire', 'vendeur', 'bailleur',
  'acquereur_proprietaire', 'acquereur_vendeur',
];

const aUnGroupe = (contact, codes) =>
  (contact?.groupes || []).some((g) => codes.includes(normaliserRole(g)));

/**
 * Détermine si un contact a un comportement d'acquéreur (= cherche à acheter / louer).
 *
 * Un contact peut être à la fois Propriétaire ET Acquéreur. Pour ça il existe le
 * type explicite "Acquéreur/Propriétaire". On accepte aussi les détections par
 * signaux concrets (recherches, critères, capacité financière) pour les anciens contacts.
 */
export function isContactAcquireur(contact) {
  if (!contact) return false;
  if (ROLES_ACQUEREUR.includes(normaliserRole(contact.type_contact))) return true;
  // Au moins une recherche définie
  const recherches = Array.isArray(contact.recherches) ? contact.recherches : [];
  if (recherches.length > 0) return true;
  // Critères de recherche legacy non vides
  const cr = contact.criteres_recherche || {};
  if (cr.operation || cr.profil || cr.budget_min || cr.budget_max ||
      cr.surface_min || cr.surface_max ||
      (cr.villes && cr.villes.length) || (cr.secteurs && cr.secteurs.length) ||
      (cr.types_biens && cr.types_biens.length)) {
    return true;
  }
  // Capacité financière déclarée
  const ic = contact.informations_confidentielles || {};
  const apport = parseFloat(ic.apport_disponible) || 0;
  const capacite = parseFloat(ic.capacite_emprunt) || 0;
  if (apport > 0 || capacite > 0) return true;
  // Tag legacy "Acquéreurs"
  return aUnGroupe(contact, ['acquereurs']);
}

/**
 * Détermine si un contact a un comportement de propriétaire (= possède des biens en mandat).
 */
export function isContactProprietaire(contact) {
  if (!contact) return false;
  const role = normaliserRole(contact.type_contact);
  if (role === 'proprietaire' || role === 'vendeur'
      || role === 'acquereur_proprietaire' || role === 'acquereur_vendeur') return true;
  // Tags legacy "Propriétaires" / "Vendeurs"
  return aUnGroupe(contact, ['proprietaires', 'vendeurs']);
}

/**
 * Mandant = celui qui confie un mandat : propriétaire, vendeur OU bailleur.
 */
export function isContactMandant(contact) {
  if (!contact) return false;
  if (normaliserRole(contact.type_contact) === 'bailleur') return true;
  if (aUnGroupe(contact, ['bailleurs'])) return true;
  return isContactProprietaire(contact);
}

/**
 * Synchronise automatiquement les groupes "Acquéreurs" / "Propriétaires" en fonction
 * du type_contact. Retire l'autre tag si non pertinent.
 * Préserve tous les autres groupes/tags personnalisés.
 *
 * Exemples :
 *   type "Acquéreur"             → groupes contient "Acquéreurs", retire "Propriétaires"
 *   type "Propriétaire"          → groupes contient "Propriétaires", retire "Acquéreurs"
 *   type "Acquéreur/Propriétaire" → groupes contient les deux
 *   autre (Prospect, Notaire…)    → groupes ne contient ni l'un ni l'autre
 */
export function syncContactTagsByType(contact) {
  if (!contact) return contact;
  const groupes = (contact.groupes || []).filter((g) => {
    const role = normaliserRole(g);
    return role !== 'acquereurs' && role !== 'proprietaires';
  });
  const role = normaliserRole(contact.type_contact);
  if (role === 'acquereur') groupes.push('Acquéreurs');
  else if (role === 'proprietaire') groupes.push('Propriétaires');
  else if (role === 'acquereur_proprietaire') {
    groupes.push('Acquéreurs');
    groupes.push('Propriétaires');
  }
  return { ...contact, groupes };
}


/**
 * lot DE — numéro au format international pour WhatsApp : chiffres seuls, 0 initial → 33.
 * Règle UNIQUE utilisée par la liste des contacts et la fiche contact.
 */
export function numeroWhatsapp(brut) {
  const chiffres = (brut || '').replace(/\D/g, '');
  if (!chiffres) return '';
  if (chiffres.startsWith('33')) return chiffres;
  if (chiffres.startsWith('0')) return `33${chiffres.slice(1)}`;
  return chiffres;
}

/**
 * Email principal d'un contact (structure `emails: [{email, principal}]`),
 * avec repli sur le champ plat legacy `email`.
 */
export function getMainEmail(contact) {
  const emails = Array.isArray(contact?.emails) ? contact.emails : [];
  const main = emails.find((e) => e?.principal) || emails[0];
  return main?.email || contact?.email || null;
}

/**
 * Téléphone principal d'un contact (structure `telephones: [{numero, principal}]`),
 * avec repli sur les champs plats legacy `telephone` / `mobile`.
 */
export function getMainPhone(contact) {
  const phones = Array.isArray(contact?.telephones) ? contact.telephones : [];
  const main = phones.find((p) => p?.principal) || phones[0];
  return main?.numero || contact?.telephone || contact?.mobile || null;
}
